import {
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  RollbackOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { PropTypes } from "@scalableminds/prop-types";
import { finishTask, peekNextTasks, requestTask } from "admin/api/tasks";
import { deleteAnnotation, downloadAnnotation, resetAnnotation } from "admin/rest_api";
import { Button, Card, Col, List, Modal, Row, Space, Tag, Tooltip } from "antd";
import classNames from "classnames";
import { AsyncButton, AsyncLink } from "components/async_clickables";
import FormattedDate from "components/formatted_date";
import LinkButton from "components/link_button";
import TransferTaskModal from "dashboard/transfer_task_modal";
import { handleGenericError } from "libs/error_handling";
import Markdown from "libs/markdown_adapter";
import Persistence from "libs/persistence";
import Request from "libs/request";
import Toast from "libs/toast";
import { compareBy } from "libs/utils";
import messages from "messages";
import { PureComponent, useContext } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import type { APIAnnotation, APITaskWithAnnotation, APIUser } from "types/api_types";
import { getSkeletonDescriptor } from "viewer/model/accessors/skeletontracing_accessor";
import { enforceActiveUser } from "viewer/model/accessors/user_accessor";
import { getVolumeDescriptors } from "viewer/model/accessors/volumetracing_accessor";
import type { WebknossosState } from "viewer/store";
import { RenderToPortal } from "viewer/view/layouting/portal_utils";
import { ActiveTabContext, RenderingTabContext } from "./dashboard_contexts";

const pageLength: number = 1000;

type TaskModeState = {
  tasks: Array<APITaskWithAnnotation>;
  loadedAllTasks: boolean;
  lastLoadedPage: number;
};
type OwnProps = {
  userId: string | null | undefined;
  isAdminView: boolean;
};
type StateProps = {
  activeUser: APIUser;
};
type Props = OwnProps & StateProps;

type State = {
  showFinishedTasks: boolean;
  isLoading: boolean;
  isTransferModalOpen: boolean;
  currentAnnotationId: string | null | undefined;
  finishedModeState: TaskModeState;
  unfinishedModeState: TaskModeState;
};
const persistence = new Persistence<Pick<State, "showFinishedTasks">>(
  {
    showFinishedTasks: PropTypes.bool,
  },
  "dashboardTaskList",
);

const convertAnnotationToTaskWithAnnotationType = (
  annotation: APIAnnotation,
): APITaskWithAnnotation => {
  const { task } = annotation;

  if (!task) {
    // This should never be the case unless tasks were deleted in the DB.
    throw new Error(
      `[Dashboard Tasks] Annotation ${annotation.id} has no task assigned. Please inform your admin.`,
    );
  }

  if (!task.type) {
    // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'type' because it is a read-only ... Remove this comment to see the full error message
    task.type = {
      summary: `[deleted] ${annotation.typ}`,
      description: "",
      settings: {
        allowedModes: "",
      },
    };
  }

  const newTask: APITaskWithAnnotation = Object.assign({}, task, {
    annotation,
  });
  return newTask;
};

class DashboardTaskListView extends PureComponent<Props, State> {
  state: State = {
    showFinishedTasks: false,
    isLoading: false,
    isTransferModalOpen: false,
    currentAnnotationId: null,
    finishedModeState: {
      tasks: [],
      loadedAllTasks: false,
      lastLoadedPage: -1,
    },
    unfinishedModeState: {
      tasks: [],
      loadedAllTasks: false,
      lastLoadedPage: -1,
    },
  };

  componentDidMount() {
    // @ts-expect-error
    this.setState(persistence.load());
    this.fetchNextPage(0);
  }

  componentDidUpdate() {
    persistence.persist(this.state);
  }

  getFinishVerb = () => (this.state.showFinishedTasks ? "Unfinished" : "Finished");
  getCurrentModeState = () =>
    this.state.showFinishedTasks ? this.state.finishedModeState : this.state.unfinishedModeState;

  setCurrentModeState = (modeShape: Partial<TaskModeState>) => {
    const { showFinishedTasks } = this.state;
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(prevState: Readonly<State>) => ... Remove this comment to see the full error message
    this.setState((prevState) => {
      const newSubState = {
        ...prevState[showFinishedTasks ? "finishedModeState" : "unfinishedModeState"],
        ...modeShape,
      };
      return {
        [showFinishedTasks ? "finishedModeState" : "unfinishedModeState"]: newSubState,
      };
    });
  };

  confirmFinish(task: APITaskWithAnnotation) {
    Modal.confirm({
      content: messages["annotation.finish"],
      onOk: async () => {
        const { annotation } = task;
        const changedAnnotationWithTask = await finishTask(annotation.id);
        const changedTask = convertAnnotationToTaskWithAnnotationType(changedAnnotationWithTask);
        this.setState((prevState) => {
          const newUnfinishedTasks = prevState.unfinishedModeState.tasks.filter(
            (t) => t.id !== task.id,
          );
          const newFinishedTasks = [changedTask].concat(prevState.finishedModeState.tasks);
          const newUnfinishedModeState = {
            ...prevState.unfinishedModeState,
            tasks: newUnfinishedTasks,
          };
          const newFinishedModeState = { ...prevState.finishedModeState, tasks: newFinishedTasks };
          return {
            unfinishedModeState: newUnfinishedModeState,
            finishedModeState: newFinishedModeState,
          };
        });
      },
    });
  }

  fetchNextPage = async (pageNumber: number) => {
    // this refers not to the pagination of antd but to the pagination of querying data from SQL
    const isFinished = this.state.showFinishedTasks;
    const previousTasks = this.getCurrentModeState().tasks;
    const url = this.props.userId
      ? `/api/users/${
          this.props.userId
        }/tasks?isFinished=${isFinished.toString()}&pageNumber=${pageNumber}`
      : `/api/user/tasks?isFinished=${isFinished.toString()}&pageNumber=${pageNumber}`;

    try {
      this.setState({
        isLoading: true,
      });
      const annotationsWithTasks = await Request.receiveJSON(url);
      const tasks = annotationsWithTasks.map(convertAnnotationToTaskWithAnnotationType);
      this.setCurrentModeState({
        loadedAllTasks:
          annotationsWithTasks.length !== pageLength || annotationsWithTasks.length === 0,
        tasks: previousTasks.concat(tasks),
        lastLoadedPage: pageNumber,
      });
    } catch (error) {
      handleGenericError(error as Error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  toggleShowFinished = () => {
    this.setState(
      (prevState) => ({
        showFinishedTasks: !prevState.showFinishedTasks,
      }),
      () => {
        if (this.getCurrentModeState().lastLoadedPage === -1) this.fetchNextPage(0);
      },
    );
  };

  openTransferModal(annotationId: string) {
    this.setState({
      isTransferModalOpen: true,
      currentAnnotationId: annotationId,
    });
  }

  renderActions = (task: APITaskWithAnnotation) => {
    const { annotation } = task;
    const isAdmin =
      this.props.activeUser.isAdmin ||
      this.props.activeUser.teams
        .filter((team) => team.isTeamManager)
        .map((team) => team.name)
        .includes(task.team);
    const label = this.props.isAdminView ? (
      <LinkButton icon={<EyeOutlined />}>View</LinkButton>
    ) : (
      <LinkButton icon={<PlayCircleOutlined />}>Open</LinkButton>
    );
    return task.annotation.state === "Finished" ? (
      <div>
        <CheckCircleOutlined className="icon-margin-right" />
        Finished
      </div>
    ) : (
      <Space orientation="vertical" size={1}>
        <Link to={`/annotations/${annotation.id}`}>{label}</Link>
        {isAdmin || this.props.isAdminView ? (
          <LinkButton onClick={() => this.openTransferModal(annotation.id)} icon={<TeamOutlined />}>
            Transfer
          </LinkButton>
        ) : null}
        {isAdmin ? (
          <>
            <AsyncLink
              onClick={() => {
                const isVolumeIncluded = getVolumeDescriptors(annotation).length > 0;
                return downloadAnnotation(annotation.id, "Task", isVolumeIncluded);
              }}
              icon={<DownloadOutlined className="icon-margin-right" />}
            >
              Download
            </AsyncLink>
            <LinkButton onClick={() => this.resetTask(annotation)} icon={<RollbackOutlined />}>
              <Tooltip title={messages["task.tooltip_explain_reset"]} placement="left">
                Reset
              </Tooltip>
            </LinkButton>
            <LinkButton onClick={() => this.cancelAnnotation(annotation)} icon={<DeleteOutlined />}>
              <Tooltip title={messages["task.tooltip_explain_reset_cancel"]} placement="left">
                Reset and Cancel
              </Tooltip>
            </LinkButton>
          </>
        ) : null}
        {this.props.isAdminView ? null : (
          <LinkButton onClick={() => this.confirmFinish(task)} icon={<CheckCircleOutlined />}>
            Finish
          </LinkButton>
        )}
      </Space>
    );
  };

  resetTask(annotation: APIAnnotation) {
    Modal.confirm({
      content: messages["task.confirm_reset"],
      cancelText: messages.no,
      okText: messages.yes,
      onOk: async () => {
        await resetAnnotation(annotation.id, annotation.typ);
        Toast.success(messages["annotation.reset_success"]);
      },
    });
  }

  cancelAnnotation(annotation: APIAnnotation) {
    const annotationId = annotation.id;
    Modal.confirm({
      content: messages["annotation.delete"],
      cancelText: messages.no,
      okText: messages.yes,
      onOk: async () => {
        await deleteAnnotation(annotationId, annotation.typ);
        this.setCurrentModeState({
          tasks: this.getCurrentModeState().tasks.filter((t) => t.annotation.id !== annotationId),
        });
      },
    });
  }

  async confirmGetNewTask(): Promise<void> {
    if (this.state.unfinishedModeState.tasks.length === 0) {
      this.getNewTask();
    } else {
      let modalContent = messages["task.request_new"];
      const likelyNextTask = await peekNextTasks();

      if (likelyNextTask != null) {
        modalContent += `\n${messages["task.peek_next"]({
          projectName: likelyNextTask.projectName,
        })}`;
      }

      Modal.confirm({
        content: modalContent,
        onOk: () => this.getNewTask(),
      });
    }
  }

  async getNewTask() {
    this.setState({
      isLoading: true,
    });

    try {
      const newTaskAnnotation = await requestTask();
      this.setState((prevState) => ({
        unfinishedModeState: {
          ...prevState.unfinishedModeState,
          tasks: prevState.unfinishedModeState.tasks.concat([
            convertAnnotationToTaskWithAnnotationType(newTaskAnnotation),
          ]),
        },
      }));
    } catch (_ex) {
      // catch exception so that promise does not fail and the modal will close
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  }

  handleTransferredTask() {
    this.setState({
      isTransferModalOpen: false,
    });

    const removeTransferredTask = (
      tasks: APITaskWithAnnotation[],
      currentAnnotationId: string | null | undefined,
    ) => tasks.filter((t) => t.annotation.id !== currentAnnotationId);

    this.setCurrentModeState({
      tasks: removeTransferredTask(this.getCurrentTasks(), this.state.currentAnnotationId),
    });
  }

  getCurrentTasks() {
    return this.getCurrentModeState().tasks;
  }

  renderPlaceholder() {
    return this.state.isLoading ? null : (
      <Row gutter={32} justify="center">
        <Col span="7">
          <Card
            variant="borderless"
            cover={<i className="drawing drawing-empty-list-tasks" style={{ translate: "15%" }} />}
            style={{ maxWidth: 460 }}
          >
            <Card.Meta
              title="Request a New Task"
              description={
                <>
                  <p style={{ marginTop: 20 }}>
                    You have no tasks assigned to you. Request a new task by clicking on the{" "}
                    <strong>Get a New Task</strong> button above.
                  </p>
                  {this.props.activeUser.isAdmin && (
                    <>
                      <p style={{ marginBottom: 30 }}>
                        Tasks are a powerful way to distribute annotation jobs among groups of users
                        as part of the WEBKNOSSOS project management.{" "}
                      </p>
                      <Space size="middle">
                        <Button
                          href="https://docs.webknossos.org/webknossos/tasks_projects/index.html"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          Learn more
                        </Button>
                        <Link to="/tasks">
                          <Button type="primary">Create new Tasks</Button>
                        </Link>
                      </Space>
                    </>
                  )}
                </>
              }
            />
          </Card>
        </Col>
      </Row>
    );
  }

  renderTaskList() {
    const tasks = this.getCurrentTasks().sort(
      compareBy<APITaskWithAnnotation>(
        (task) => (this.state.showFinishedTasks ? task.annotation.modified : task.created),
        false,
      ),
    );
    const descriptionClassName = classNames("task-type-description", {
      short: this.state.showFinishedTasks || this.props.isAdminView,
    });

    const TaskCardTitle = ({ task }: { task: APITaskWithAnnotation }) => (
      <Space>
        {task.projectName} (<FormattedDate timestamp={task.created} />)
        {getSkeletonDescriptor(task.annotation) == null ? null : (
          <Tag color="green" variant="outlined">
            skeleton
          </Tag>
        )}
        {getVolumeDescriptors(task.annotation).length === 0 ? null : (
          <Tag color="orange" variant="outlined">
            volume
          </Tag>
        )}
        {task.type.settings.allowedModes.map((mode) => (
          <Tag key={mode} variant="outlined">
            {mode}
          </Tag>
        ))}
      </Space>
    );

    const TaskCard = (task: APITaskWithAnnotation) =>
      this.state.showFinishedTasks ? (
        <Card key={task.id}>
          <Row gutter={16}>
            <Col span={7}>
              <b>Task ID:</b> {task.id}
            </Col>
            <Col span={7}>
              <b>Project:</b> {task.projectName}
            </Col>
            <Col span={7}>
              <b>Finished:</b> <FormattedDate timestamp={task.annotation.modified} />
            </Col>
            <Col span={3}>{this.renderActions(task)}</Col>
          </Row>
        </Card>
      ) : (
        <Card key={task.id} title={<TaskCardTitle task={task} />}>
          <Row gutter={16}>
            <Col span={16}>
              <div className={descriptionClassName}>
                <Markdown>{task.type.description}</Markdown>
              </div>
            </Col>
            <Col span={8}>
              <p
                style={{
                  marginBottom: 14,
                }}
              >
                <b>Task ID:</b> {task.id}
                <br />
                <b>Task Type:</b> {task.type.summary}
              </p>
              {this.renderActions(task)}
            </Col>
          </Row>
        </Card>
      );

    return (
      <List
        dataSource={tasks}
        pagination={{
          defaultPageSize: 50,
        }}
        loading={this.state.isLoading}
        renderItem={TaskCard}
        locale={{
          emptyText: this.renderPlaceholder(),
        }}
      />
    );
  }

  render() {
    return (
      <div>
        <TopBar
          confirmGetNewTask={() => this.confirmGetNewTask()}
          isAdminView={this.props.isAdminView}
          userId={this.props.userId}
          toggleShowFinished={this.toggleShowFinished}
          getFinishVerb={this.getFinishVerb}
        />
        {this.state.showFinishedTasks ? <h3>My Finished Tasks</h3> : null}
        {this.renderTaskList()}
        <div
          style={{
            textAlign: "right",
          }}
        >
          {!this.getCurrentModeState().loadedAllTasks ? (
            <Link
              to="#"
              onClick={() => this.fetchNextPage(this.getCurrentModeState().lastLoadedPage + 1)}
            >
              Load more Tasks
            </Link>
          ) : null}
        </div>
        <TransferTaskModal
          isOpen={this.state.isTransferModalOpen}
          annotationId={this.state.currentAnnotationId}
          onCancel={() =>
            this.setState({
              isTransferModalOpen: false,
            })
          }
          onChange={() => this.handleTransferredTask()}
        />
      </div>
    );
  }
}

function TopBar({
  confirmGetNewTask,
  isAdminView,
  userId,
  toggleShowFinished,
  getFinishVerb,
}: {
  confirmGetNewTask: () => Promise<void>;
  isAdminView: boolean;
  userId: string | null | undefined;
  toggleShowFinished: () => void;
  getFinishVerb: () => string;
}) {
  const activeTab = useContext(ActiveTabContext);
  const renderingTab = useContext(RenderingTabContext);

  const content = (
    <Space>
      <AsyncButton
        type="primary"
        icon={<UserAddOutlined />}
        onClick={confirmGetNewTask}
        disabled={isAdminView && userId != null}
      >
        Get a New Task
      </AsyncButton>
      <Button onClick={toggleShowFinished}>Show {getFinishVerb()} Tasks Only</Button>
    </Space>
  );

  return (
    <RenderToPortal portalId="dashboard-TabBarExtraContent">
      {activeTab === renderingTab ? content : null}
    </RenderToPortal>
  );
}

const mapStateToProps = (state: WebknossosState): StateProps => ({
  activeUser: enforceActiveUser(state.activeUser),
});

const connector = connect(mapStateToProps);
export default connector(DashboardTaskListView);
